import { useState, useEffect, useRef } from "react";
import { useParams, useLocation } from "react-router-dom";
import ChatBot from "./ChatBot";
import { BASE_URL, sendSummaryWhatsapp, downloadSummaryPdf, downloadSummaryAudio, getSessionHistory } from "../api";
// Removed Howler; using native HTMLAudio for playback
import { Toast } from 'primereact/toast';
import ShareModal from "./ShareModal";
import AgentSidebar from "./AgentSidebar";
import UploadInfoBar from "./UploadInfoBar";
import SessionControls from "./SessionControls";
import SummaryCard from "./SummaryCard";
import ChatHistory from "./ChatHistory";
import { playAudioFromBase64, stopActiveAudioContextSource, float32ToPCM16Bytes, wrapPcm16ToWav } from "../utils/audio";
import { ensureExclusive, setGlobalAudio } from '../utils/audioManager'
import { LiveAvatarSession } from "@heygen/liveavatar-web-sdk";

const THINKING_STATES = [
  "Thinking",
  "Analyzing",
  "Processing",
  // "Generating response..."
];

export default function ConversationPage() {
  // Ensure unique, stable IDs for React keys and message tracking
  const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const { sessionId } = useParams();
  const location = useLocation();
  const [uploadResponse, setUploadResponse] = useState(null);
  const [agentData, setAgentData] = useState(null);
  const [chatMessage, setChatMessage] = useState("");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [voiceGender, setVoiceGender] = useState("female");
  const [includeAudio, setIncludeAudio] = useState(true);
  const [playingAudioId, setPlayingAudioId] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [currentThinkingState, setCurrentThinkingState] = useState("");
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [thinkingIndex, setThinkingIndex] = useState(0);
  const audioRef = useRef(null);
  const base64ObjectUrlRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isTalkToAgent, setIsTalkToAgent] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [isSending, setIsSending] = useState(false);
  const toast = useRef(null);
  // Live voice session streaming refs
  const liveVoiceWsRef = useRef(null);
  const liveAudioChunksRef = useRef([]);
  const liveLastResponseIdRef = useRef(null);
  const liveReadyRef = useRef(false);
  const transcriptionTimeoutRef = useRef(null);

  // HeyGen LiveAvatar integration
  const unlockAudio = async () => {
    const video = heygenVideoRef.current;
    if (!video) return;

    try {
      video.muted = false;
      await video.play();
      setAudioUnlocked(true);
    } catch (e) {
      console.error("Audio unlock failed", e);
    }
  };
  const useHeyGen = true;
  const [heygenReady, setHeygenReady] = useState(false);
  const [heygenLoading, setHeygenLoading] = useState(false);
  const [heygenError, setHeygenError] = useState(null);
  const [heygenNeedsInteraction, setHeygenNeedsInteraction] = useState(false);
  const [heygenStarted, setHeygenStarted] = useState(false); // Track if avatar has been started
  const heygenVideoRef = useRef(null);
  const heygenAudioRef = useRef(null);
  const heygenClientRef = useRef(null);
  const heygenIntroPlayedRef = useRef(false);
  const heygenInitializedRef = useRef(false);

  // Create session token for LiveAvatar
  const createHeyGenSessionToken = async () => {
    try {
      const res = await fetch("https://api.liveavatar.com/v1/sessions/token", {
        method: "POST",
        headers: {
          "X-API-KEY": process.env.REACT_APP_LIVEAVATAR_API_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          mode: "FULL", // Changed from CUSTOM to FULL - FULL mode supports repeat() method
          avatar_id: process.env.REACT_APP_LIVEAVATAR_AVATAR_ID,
          avatar_persona: {
            context_id: process.env.REACT_APP_LIVEAVATAR_CONTEXT_ID,
            voice_id: process.env.REACT_APP_LIVEAVATAR_VOICE_ID,
            language: "en",
          },
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to create session token: ${res.status}`);
      }

      const data = await res.json();
      return data.data.session_token;
    } catch (error) {
      console.error("Error creating session token:", error);
      setHeygenError(error.message);
      throw error;
    }
  };

  // Start HeyGen Avatar (called by Start button)
  const startHeyGenAvatar = async () => {
    if (heygenStarted) {
      console.log("Avatar already started");
      return;
    }
    setHeygenStarted(true);
    await initHeyGen({ forceFrontendToken: true });
  };

  // Stop HeyGen Avatar (called by Stop button)
  const stopHeyGenAvatar = async () => {
    try {
      if (heygenClientRef.current) {
        await heygenClientRef.current.stop();
        heygenClientRef.current = null;
      }
      setHeygenReady(false);
      setHeygenStarted(false);
      heygenInitializedRef.current = false;
      heygenIntroPlayedRef.current = false;
      setAudioUnlocked(false);
      console.log("Avatar stopped");
    } catch (error) {
      console.error("Error stopping avatar:", error);
    }
  };

  // Initialize HeyGen LiveAvatar
  const initHeyGen = async ({ forceFrontendToken = false } = {}) => {
    if (heygenInitializedRef.current) return true;

    setHeygenLoading(true);
    setHeygenError(null);

    try {
      const token = await createHeyGenSessionToken();
      const client = new LiveAvatarSession(token);
      heygenClientRef.current = client;

      // Track if video has been attached to prevent duplicate attachments
      let videoAttached = false;
      
      // Promise to track when connection is ready for attachment
      let connectionReadyResolve;
      const connectionReadyPromise = new Promise((resolve) => {
        connectionReadyResolve = resolve;
      });

      // Helper function to attach video (only once)
      const attachVideoSafely = () => {
        if (videoAttached) {
          console.log("⏭️ Video already attached, skipping");
          return;
        }
        
        if (!heygenVideoRef.current) {
          console.warn("⚠️ Video ref not available for attachment");
          return;
        }
        
        try {
          client.attach(heygenVideoRef.current);
          videoAttached = true;
          console.log("✅ Video element attached successfully");
        } catch (error) {
          if (error.message?.includes('not yet ready') || error.message?.includes('Stream')) {
            console.log("⏳ Stream not ready yet, will retry when 'connected' event fires");
          } else {
            console.warn("⚠️ Video attachment error:", error.message);
          }
        }
      };

      // Add event listeners
      client.on("connected", () => {
        console.log("LiveAvatar transport connected");
        
        // Attach video element when connected (stream is ready)
        attachVideoSafely();
        
        // Mark as ready as soon as connected (don't wait for first speech)
        if (!heygenInitializedRef.current) {
          heygenInitializedRef.current = true;
          setHeygenReady(true);
          console.log("Avatar is now READY for interaction");
        }
        
        // Resolve the promise so we know connection is ready
        if (connectionReadyResolve) {
          connectionReadyResolve();
        }
      });

      client.on("avatar_start_talking", () => {
        console.log("Avatar started speaking");
      });

      client.on("avatar_stop_talking", () => {
        console.log("Avatar stopped speaking");
        // Ensure ready state is set (in case connected event didn't fire)
        if (!heygenInitializedRef.current) {
          heygenInitializedRef.current = true;
          setHeygenReady(true);
          console.log("Avatar is now READY for interaction");
        }
      });
      

      client.on("disconnected", () => {
        console.warn("Avatar disconnected from room");
        setHeygenReady(false);
      });

      client.on("error", (err) => {
        console.error("Avatar error:", err);
        setHeygenError(err.message || "Avatar error occurred");
      });

      await client.start();
      
      // Wait for connection to be ready - the 'connected' event will handle attachment
      // Don't try to attach here - only attach in the 'connected' event handler
      // This prevents "Stream is not yet ready" errors
      let connectionResolved = false;
      try {
        await Promise.race([
          connectionReadyPromise.then(() => { connectionResolved = true; }),
          new Promise((resolve) => setTimeout(resolve, 5000))
        ]);
        if (connectionResolved) {
          console.log("✅ Connection ready, video should be attached by 'connected' event handler");
        } else {
          console.log("⏳ Connection promise timed out, will try fallback attachment");
        }
      } catch (error) {
        console.warn("Connection ready promise error:", error);
      }
      
      // Fallback: If video still wasn't attached after waiting, try to attach now
      // This handles cases where the 'connected' event fired before we set up the listener
      // or if the event doesn't fire at all
      if (!videoAttached && heygenVideoRef.current) {
        console.log("🔄 Attempting fallback attachment (connected event may have already fired)");
        
        // Wait a bit for stream to be ready (we saw "setStreamState active" in logs)
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        // Try to attach with retries
        let retries = 0;
        const maxRetries = 3;
        while (!videoAttached && retries < maxRetries) {
          try {
            attachVideoSafely();
            if (videoAttached) {
              console.log("✅ Video attached successfully via fallback");
              break;
            }
          } catch (error) {
            if (error.message?.includes('not yet ready')) {
              retries++;
              if (retries < maxRetries) {
                console.log(`⏳ Stream not ready, retrying... (${retries}/${maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            } else {
              console.warn("⚠️ Attachment error:", error.message);
              break;
            }
          }
        }
        
        if (!videoAttached) {
          console.warn("⚠️ Video attachment failed - stream may attach automatically later");
        }
      }

      // Mark as ready after start completes (connected event may have already fired)
      if (!heygenInitializedRef.current) {
        heygenInitializedRef.current = true;
        setHeygenReady(true);
        console.log("Avatar initialized and ready after start()");
      }

      setHeygenLoading(false);
      return true;
    } catch (error) {
      console.error("Failed to initialize HeyGen:", error);
      setHeygenLoading(false);
      setHeygenError(error.message);
      return false;
    }
  };

  // Make avatar speak text
  // const heygenTalk = async (text) => {
  //   if (!audioUnlocked) {
  //     console.warn("Audio not unlocked yet");
  //     return;
  //   }

  //   await heygenClientRef.current.interrupt();
  //   await heygenClientRef.current.speakText({
  //     text,
  //     task_type: "talk",
  //   });
  // };

  const heygenTalk = async (text) => {
    if (!heygenClientRef.current) {
      console.warn("HeyGen client not ready");
      return;
    }
    if (!text?.trim()) {
      console.warn("heygenTalk called with empty text");
      return;
    }
  
    // Log what we're about to send
    console.log("🎤 heygenTalk called with text:", text);
    console.log("🎤 Text length:", text.length);
    console.log("🎤 Full text:", JSON.stringify(text));
  
    // Auto-unlock audio if needed
    if (!audioUnlocked) {
      console.log("Auto-unlocking audio for avatar speech");
      try {
        await unlockAudio();
      } catch (e) {
        console.warn("Failed to auto-unlock audio", e);
      }
    }
  
    try {
      await heygenClientRef.current.interrupt();
      // Use repeat() method - should work in FULL mode
      console.log("🎤 Calling repeat() with text:", text.substring(0, 50) + "...");
      await heygenClientRef.current.repeat(text);
      console.log("✅ Avatar speech triggered successfully with full text");
    } catch (e) {
      console.error("❌ HeyGen speak failed", e);
      // Log the error details for debugging
      console.error("Error details:", {
        message: e.message,
        stack: e.stack,
        textLength: text?.length,
        textPreview: text?.substring(0, 100),
        clientMethods: Object.keys(heygenClientRef.current || {}),
      });
    }
  };
  
  // Enable audio interaction
  const heygenEnableAudio = () => {
    if (heygenAudioRef.current) {
      heygenAudioRef.current.muted = false;
      setHeygenNeedsInteraction(false);
    }
  };

  // Handle audio stream from LiveAvatar
  useEffect(() => {
    if (!heygenClientRef.current || !heygenAudioRef.current) return;

    // LiveAvatar SDK automatically handles audio streaming to the video element
    // The audio element in AgentSidebar is for user interaction control
    const handleAudioInteraction = () => {
      if (heygenAudioRef.current) {
        // When user interacts with audio, ensure it's not muted
        heygenAudioRef.current.muted = false;
      }
    };

    // Add event listener for audio interaction
    if (heygenAudioRef.current) {
      heygenAudioRef.current.addEventListener('play', handleAudioInteraction);
    }

    return () => {
      if (heygenAudioRef.current) {
        heygenAudioRef.current.removeEventListener('play', handleAudioInteraction);
      }
    };
  }, [heygenReady]);

  // Removed auto-initialization - now requires Start button click
  // useEffect(() => {
  //   let mounted = true;
  //   (async () => {
  //     if (useHeyGen && !heygenReady && !heygenLoading) {
  //       const ok = await initHeyGen({ forceFrontendToken: true });
  //       if (mounted) {
  //         setHeygenReady(!!ok);
  //       }
  //     }
  //   })();
  //   return () => {
  //     mounted = false;
  //     if (heygenClientRef.current) {
  //       heygenClientRef.current.stop().catch(console.error);
  //     }
  //   };
  // }, [useHeyGen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (heygenClientRef.current) {
        heygenClientRef.current.stop().catch(console.error);
      }
    };
  }, []);

  // Play an introduction once avatar is ready
  useEffect(() => {
    if (!useHeyGen) return;
    if (!heygenReady) return;
    if (!heygenStarted) return; // Only play intro if avatar was started via button
    if (heygenIntroPlayedRef.current) return;
    
    const name = agentData?.agent_name || agentData?.name || 'Dr. Anika';
    const intro = `Hello! I am ${name}. I can analyze your medical documents and answer questions about obesity treatments, medications, and research. How can I help you today?`;
    
    console.log("🎤 Playing intro:", intro);
    
    // Wait a bit for avatar to be fully ready before speaking
    setTimeout(() => {
      try {
        heygenTalk(intro);
        heygenIntroPlayedRef.current = true;
      } catch (e) {
        console.error("Failed to play intro:", e);
      }
    }, 1000);
  }, [useHeyGen, heygenReady, heygenStarted, agentData]);

  // Removed unnecessary re-attach useEffect - video is already attached after start()
  // The duplicate attach was causing "Stream is not yet ready" warnings

  const handleSendWhatsapp = async () => {
    setIsSending(true);
    try {
      const response = await sendSummaryWhatsapp(sessionId, whatsappNumber, countryCode);
      toast.current.show({
        severity: 'success',
        summary: 'Message Sent',
        detail: 'Summary sent successfully on WhatsApp!',
        life: 3000,
      });
      setShowShareModal(false);
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Failed',
        detail: 'Unable to send WhatsApp message.',
        life: 3000,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleDownload = async (type) => {
    if (!sessionId) return;
    try {
      const resp = await downloadSummaryPdf(sessionId)
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `summary-${sessionId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.current?.show({ severity: 'success', summary: 'Download started', life: 2000 });
    } catch (e) {
      toast.current?.show({ severity: 'error', summary: 'Download failed', life: 3000 });
    }
  };
  const fetchHistory = async (sessionId) => {
    try {
      const resp = await getSessionHistory(sessionId);
      const history = resp?.history || [];

      if (Array.isArray(history) && history.length) {
        const sorted = [...history].sort(
          (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
        );

        const transformed = [];
        sorted.forEach((h, idx) => {
          const baseTs = h.timestamp || new Date().toISOString();

          if (h.type === "user") {
            transformed.push({
              id: h.id ?? Date.now() + idx * 2,
              type: "user",
              message: h.message ?? h.question ?? "",
              timestamp: baseTs,
            });
            return;
          }

          if (h.type === "ai" || h.type === "assistant") {
            transformed.push({
              id: h.id ?? Date.now() + idx * 2 + 1,
              type: "ai",
              message: h.message ?? h.answer ?? "",
              timestamp: baseTs,
              audio: h.audio_base64 || h.audio || null,
              sources: h.sources || [],
            });
            return;
          }
          if (h.question) {
            transformed.push({
              id: Date.now() + idx * 2,
              type: "user",
              message: h.question,
              timestamp: baseTs,
            });
          }

          if (h.answer || h.transcription || h.text) {
            transformed.push({
              id: Date.now() + idx * 2 + 1,
              type: "ai",
              message: h.answer || h.transcription || h.text || "",
              timestamp: baseTs,
              audio: h.audio_base64 || h.audio || null,
              sources: h.sources || [],
            });
          }
        });
        transformed.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );

        setChatHistory(transformed);
      }
    } catch (err) {
      console.error("Failed to load session history:", err);
    }
  };

  useEffect(() => {
    return () => {
      if (base64ObjectUrlRef.current) {
        URL.revokeObjectURL(base64ObjectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isLoading) return;

    const interval = setInterval(() => {
      setThinkingIndex((prev) => {
        const next = (prev + 1) % THINKING_STATES.length;
        setCurrentThinkingState(THINKING_STATES[next]);
        return next;
      });
    }, 10000);

    return () => clearInterval(interval);
  }, [isLoading]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [chatHistory, isLoading]);

  useEffect(() => {
    if (location.state?.uploadResponse) {
      setUploadResponse(location.state.uploadResponse);
    }
    if (location.state?.agentId && location.state?.agentName) {
      setAgentData({
        id: location.state.agentId,
        name: location.state.agentName,
        description: location.state.description,
      });
    }
    sessionId && fetchHistory(sessionId)

    if (location.state?.isTalkToAgent) {
      setIsTalkToAgent(true);
    }
  }, [location.state, sessionId]);


  const hideAISummary = !uploadResponse;



  const pauseAudio = () => {
    const audioEl = audioRef.current;
    // Stop any AudioContext-based playback
    try { stopActiveAudioContextSource(); } catch { }
    if (audioEl && !audioEl.paused) {
      audioEl.pause();
      setIsPlaying(false);
    }
  };

  const stopAudio = () => {
    const audioEl = audioRef.current;
    // Stop any AudioContext-based playback
    try { stopActiveAudioContextSource(); } catch { }
    if (audioEl) {
      audioEl.pause();
      audioEl.currentTime = 0;
      setIsPlaying(false);
      setPlayingAudioId(null);
      if (base64ObjectUrlRef.current) {
        try { URL.revokeObjectURL(base64ObjectUrlRef.current); } catch { }
        base64ObjectUrlRef.current = null;
      }
    }
  };

  const playAudioFromUrl = (audioUrl, audioId) => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio();
      }

      // If same audio is paused, just resume
      if (playingAudioId === audioId && audioRef.current.src === audioUrl && !isPlaying) {
        try { ensureExclusive(audioRef.current); setGlobalAudio(audioRef.current); } catch { }
        audioRef.current.play();
        setIsPlaying(true);
        return;
      }

      // Different audio - stop and play new one
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = audioUrl;
      try { ensureExclusive(audioRef.current); setGlobalAudio(audioRef.current); } catch { }
      setPlayingAudioId(audioId);
      setIsPlaying(true);
      audioRef.current.play();

      audioRef.current.onended = () => {
        setPlayingAudioId(null);
        setIsPlaying(false);
      };

      audioRef.current.onpause = () => {
        if (audioRef.current && (audioRef.current.currentTime === 0 || audioRef.current.ended)) {
          setPlayingAudioId(null);
          setIsPlaying(false);
        }
      };
    } catch (error) {
      console.error("Error playing audio:", error);
      alert("Error playing audio. Please try again.");
    }
  };

  const handleReadAloud = () => {
    const audioEl = audioRef.current;

    if (isPlaying && playingAudioId === 'summary') {
      pauseAudio();
    } else if (audioEl && audioEl.src && playingAudioId === 'summary' && audioEl.currentTime > 0 && !audioEl.ended) {
      setIsPlaying(true);
      try { ensureExclusive(audioEl); setGlobalAudio(audioEl); } catch { }
      audioEl.play().catch(err => console.error("Error resuming audio:", err));
    } else {
      if (uploadResponse?.audio_base64) {
        const audioBase64 = uploadResponse.audio_base64;
        playAudioFromBase64(audioBase64, 'summary', 'audio/mpeg', {
          audioRef,
          base64ObjectUrlRef,
          setPlayingAudioId,
          setIsPlaying,
        });
      } else {
        console.log("No audio available");
        alert("No audio summary available");
      }
    }
  };

  const handleRestartAudio = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      if (!isPlaying) {
        setIsPlaying(true);
        audioRef.current.play();
      }
    }
  };

  const seekAudio = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.currentTime + seconds, audioRef.current.duration));
    }
  };
  const handleStreamResponse = (update) => {
    console.log('[ConversationPage] handleStreamResponse called with:', update);
    if (!update) return;
    setChatHistory((prev) => {
      // --- Case 1: functional update (streaming text/audio chunks) ---
      if (typeof update === "function") {
        const last = prev[prev.length - 1];
        if (!last || last.type !== 'ai') {
          // No AI bubble yet in current turn — create one
          const base = {
            id: genId(),
            type: 'ai',
            question: '',
            answer: '',
            message: '',
            audio: null,
            sources: [],
            streaming: true,
            timestamp: new Date().toISOString(),
          };
          const applied = update(base) || {};
          const newMsg = {
            id: base.id,
            type: 'ai',
            question: '',
            answer: applied.answer || '',
            message: applied.answer || applied.message || '',
            audio: applied.audio ?? applied.audioBase64 ?? null,
            sources: typeof applied.sources !== 'undefined' ? applied.sources : base.sources,
            streaming: typeof applied.streaming === 'boolean' ? applied.streaming : true,
            timestamp: base.timestamp,
          };
          return [...prev, newMsg];
        } else {
          // Update the existing AI bubble in-place (text or audio)
          const applied = update(last) || {};
          const merged = {
            ...last,
            ...applied,
            message: applied?.answer || applied?.message || last?.message || '',
            answer: applied?.answer || last?.answer || '',
            audio: applied?.audio ?? applied?.audioBase64 ?? last?.audio ?? null,
            sources: typeof applied?.sources !== 'undefined' ? applied.sources : last.sources,
            streaming: typeof applied?.streaming === 'boolean' ? applied.streaming : last.streaming,
          };
          const newArr = [...prev];
          newArr[newArr.length - 1] = merged;
          return newArr;
        }
      }

      // --- Case 2: direct object (new message start, audio_complete, text_complete meta, etc.) ---
      const last = prev[prev.length - 1];
      const incomingType = update.type || 'ai';

      // Treat text_complete as a meta event used to drive avatar speech,
      // without creating or altering chat bubbles here.
      if (incomingType === 'text_complete') {
        const speakText = update?.answer || update?.message || '';
        console.log('[ConversationPage] meta text_complete received', {
          speakText: speakText?.substring(0, 100) + '...',
          useHeyGen,
          heygenReady,
          audioUnlocked,
          hasClient: !!heygenClientRef.current,
        });
        // Only require useHeyGen and client exists; heygenTalk will handle audio unlock
        if (useHeyGen && heygenClientRef.current && speakText) {
          console.log('[ConversationPage] calling heygenTalk from text_complete');
          try { 
            heygenTalk(speakText); 
          } catch (e) { 
            console.error('heygenTalk failed from text_complete', e); 
          }
        } else {
          console.warn('[ConversationPage] conditions not met for avatar speech on text_complete', {
            useHeyGen,
            hasClient: !!heygenClientRef.current,
            hasText: !!speakText,
          });
        }
        return prev;
      }
      const incomingMsg = {
        id: update.id || genId(),
        type: incomingType,
        question: update.question || '',
        // For search_complete, no answer text may be present; keep message empty and rely on sources UI
        answer: update.type === 'search_complete' ? '' : (update.answer || update.transcription || update.text || ''),
        message: update.type === 'search_complete' ? '' : (update.answer || update.transcription || update.text || ''),
        audio: typeof update.audio !== 'undefined' ? update.audio : (update.audioBase64 || null),
        // Do not clear sources if update lacks sources
        sources: typeof update.sources !== 'undefined' ? update.sources : (last?.sources ?? []),
        streaming: typeof update.streaming === 'boolean' ? update.streaming : (incomingType === 'ai'),
        timestamp: new Date().toISOString(),
      };

      // Maintain sequence: user then ai — append user always; ai appends only after user
      if (!last || last.type === 'user' || incomingType === 'user') {
        console.log('newMsg', incomingMsg);
        return [...prev, incomingMsg];
      } else {
        // Merge into the last AI bubble
        const merged = {
          ...last,
          ...incomingMsg,
          message: incomingMsg?.message || last?.message || '',
          answer: incomingMsg?.answer || last?.answer || '',
          audio: typeof incomingMsg?.audio !== 'undefined' ? incomingMsg.audio : last?.audio ?? null,
          // Only replace sources if incoming provides them
          sources: typeof incomingMsg?.sources !== 'undefined' ? incomingMsg.sources : last.sources,
          streaming: typeof incomingMsg?.streaming === 'boolean' ? incomingMsg.streaming : last.streaming,
        };
        const newArr = [...prev];
        newArr[newArr.length - 1] = merged;
        return newArr;
      }
    });
    // Manage loading based on streaming flag or audio presence
    if (typeof update === 'function') {
      try {
        // Peek updated streaming state by applying to a dummy message
        const result = update({
          id: Date.now(), type: 'ai', question: '', answer: '', message: '',
          audioBase64: null, sources: [], streaming: true
        });
        if (typeof result?.streaming === 'boolean') {
          setIsLoading(result.streaming);
          setCurrentThinkingState(result.streaming ? "Generating" : "");
        }
      } catch { }
    } else {
      if (typeof update?.streaming === 'boolean') {
        setIsLoading(update.streaming);
        setCurrentThinkingState(update.streaming ? "Generating" : "");
      }
    }

    if (typeof update !== 'function') {
      const endTypes = ['text_complete', 'audio_complete', 'chat_complete', 'search_complete', 'error'];
      const isEndType = endTypes.includes(update?.type);
      const explicitlyNotStreaming = update?.streaming === false;
      if (isEndType || explicitlyNotStreaming) {
        setIsLoading(false);
        setCurrentThinkingState("");
      }
    }
    const audioBase64 = update.audio || update.audioBase64;
    const audioMime = update.mime || update.audioMime || 'auto';
    if (audioBase64 && !useHeyGen) {
      try {
        playAudioFromBase64(audioBase64, Date.now(), audioMime, {
          audioRef,
          base64ObjectUrlRef,
          setPlayingAudioId,
          setIsPlaying,
        });
        // Audio present implies streaming completed; ensure loading cleared
        setIsLoading(false);
        setCurrentThinkingState("");
      } catch (err) {
        setIsLoading(false);
        console.warn("Audio playback failed:", err);
      }
    }

  };




  const startRecording = async () => {
    try {
      // Request mic permission and get audio stream upfront
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      let recorder = null; // fallback recorder (WebM/Opus)
      const chunks = [];
      // PCM capture via AudioContext for WAV
      let audioCtx = null;
      let sourceNode = null;
      let processor = null;
      let pcmBuffers = [];
      let inputSampleRate = 16000; // default; will read from AudioContext
      // Open WS for streaming using the same session stream protocol as chat
      try {
        setThinkingIndex(0);
        setCurrentThinkingState("");

        const wsBase = BASE_URL.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
        const accessToken = localStorage.getItem('access_token');
        // Add token as query parameter for WebSocket authentication
        let sessionWsUrl = `${wsBase}/ws/query/${sessionId}`;
        if (accessToken) {
          sessionWsUrl += `?token=${encodeURIComponent(accessToken)}`;
        }

        const ws = new WebSocket(sessionWsUrl);
        ws.binaryType = 'arraybuffer';
        liveVoiceWsRef.current = ws;
        liveReadyRef.current = false;

        ws.onopen = () => {
          console.info('[Voice WS] connected');
          // Voice chat initialization (as specified)
          const voiceMessage = {
            action: 'voice_chat',
            include_audio: !!includeAudio,
            voice_gender: voiceGender || 'female',
            language_code: 'en-US',
            // Prefer WAV for widest backend compatibility
            audio_format: 'audio/wav',
          };
          try {
            console.log('[Voice WS] Sending init:', voiceMessage);
            ws.send(JSON.stringify(voiceMessage));
          } catch { }
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            console.log('[Voice WS] received:', message);
            const type = message?.type;
            switch (type) {
              case 'ready_for_audio': {
                liveReadyRef.current = true;
                // Start recording only after server signals ready;
                // Capture PCM via AudioContext and buffer samples, then encode WAV on stop
                try {
                  audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                  inputSampleRate = audioCtx.sampleRate || 48000;
                  sourceNode = audioCtx.createMediaStreamSource(stream);
                  // Use ScriptProcessorNode for broad compatibility
                  const bufferSize = 4096;
                  processor = audioCtx.createScriptProcessor(bufferSize, 1, 1);
                  processor.onaudioprocess = (e) => {
                    const input = e.inputBuffer.getChannelData(0);
                    // Copy the buffer to avoid retaining AudioBuffer references
                    pcmBuffers.push(new Float32Array(input));
                  };
                  sourceNode.connect(processor);
                  processor.connect(audioCtx.destination);

                  setIsRecording(true);
                  setMediaRecorder({
                    stop: () => {
                      try {
                        // Disconnect audio graph
                        processor && processor.disconnect();
                        sourceNode && sourceNode.disconnect();
                        if (audioCtx && audioCtx.state !== 'closed') audioCtx.close();
                      } catch { }

                      // Concatenate Float32 samples
                      const totalLength = pcmBuffers.reduce((sum, arr) => sum + arr.length, 0);
                      const merged = new Float32Array(totalLength);
                      let offset = 0;
                      for (const arr of pcmBuffers) {
                        merged.set(arr, offset);
                        offset += arr.length;
                      }
                      pcmBuffers = [];

                      // Convert to PCM16 and wrap to WAV
                      const pcm16Bytes = float32ToPCM16Bytes(merged);
                      const wavBlob = wrapPcm16ToWav(pcm16Bytes, inputSampleRate);

                      try {
                        console.debug('[Voice WS] WAV blob', { size: wavBlob.size, type: wavBlob.type, sampleRate: inputSampleRate });
                      } catch { }

                      const audioUrl = URL.createObjectURL(wavBlob);
                      const audioMessageId = genId();
                      const audioMessage = {
                        id: audioMessageId,
                        type: 'user',
                        message: '🎤 Audio message',
                        timestamp: new Date().toISOString(),
                        isAudio: true,
                        audioUrl,
                      };
                      setChatHistory(prev => [...prev, audioMessage]);

                      // Send WAV binary
                      (async () => {
                        try {
                          if (liveVoiceWsRef.current && liveVoiceWsRef.current.readyState === WebSocket.OPEN) {
                            if (wavBlob && wavBlob.size >= 4096) {
                              const arrayBuffer = await wavBlob.arrayBuffer();
                              liveVoiceWsRef.current.send(arrayBuffer);
                            } else {
                              const errorMessage = {
                                id: genId(),
                                type: 'ai',
                                message: '⚠️ Voice input failed: No speech detected in audio. Please try again.',
                                timestamp: new Date().toISOString(),
                              };
                              setChatHistory(prev => [...prev, errorMessage]);
                            }
                          }
                        } catch (error) {
                          console.error('Error sending WAV over WS:', error);
                          const errorMessage = { id: genId(), type: 'ai', message: 'Sorry, I couldn\'t send your audio. Please try again.', timestamp: new Date().toISOString() };
                          setChatHistory(prev => [...prev, errorMessage]);
                        } finally {
                          // Stop mic
                          try { stream.getTracks().forEach(track => track.stop()); } catch { }
                          setIsRecording(false);
                          setMediaRecorder(null);
                        }
                      })();
                    }
                  });
                } catch (err) {
                  console.warn('[Voice WS] Failed to initialize PCM capture, falling back to MediaRecorder', err);
                  // Fallback: use MediaRecorder (WebM/Opus)
                  if (!recorder) {
                    try {
                      recorder = new MediaRecorder(stream, { mimeType: mime });
                    } catch (e) {
                      recorder = new MediaRecorder(stream);
                    }
                    recorder.ondataavailable = async (evt) => {
                      if (evt.data && evt.data.size > 1024) {
                        chunks.push(evt.data);
                      }
                    };
                    recorder.onstop = async () => {
                      const audioBlob = new Blob(chunks, { type: mime });
                      try { console.debug('[Voice WS] recorded blob (fallback)', { size: audioBlob.size, type: audioBlob.type, chunks: chunks.length, mime }); } catch { }
                      const audioUrl = URL.createObjectURL(audioBlob);
                      const audioMessageId = genId();
                      const audioMessage = { id: audioMessageId, type: 'user', message: '🎤 Audio message', timestamp: new Date().toISOString(), isAudio: true, audioUrl };
                      setChatHistory(prev => [...prev, audioMessage]);
                      try {
                        if (liveVoiceWsRef.current && liveVoiceWsRef.current.readyState === WebSocket.OPEN) {
                          if (audioBlob && audioBlob.size >= 4096) {
                            const arrayBuffer = await audioBlob.arrayBuffer();
                            liveVoiceWsRef.current.send(arrayBuffer);
                          } else {
                            const errorMessage = { id: genId(), type: 'ai', message: '⚠️ Voice input failed: No speech detected in audio. Please try again.', timestamp: new Date().toISOString() };
                            setChatHistory(prev => [...prev, errorMessage]);
                          }
                        }
                      } catch (error) {
                        console.error('Error sending audio blob over WS:', error);
                        const errorMessage = { id: genId(), type: 'ai', message: 'Sorry, I couldn\'t send your audio. Please try again.', timestamp: new Date().toISOString() };
                        setChatHistory(prev => [...prev, errorMessage]);
                      }
                      try { stream.getTracks().forEach(track => track.stop()); } catch { }
                      setIsRecording(false);
                      setMediaRecorder(null);
                    };
                    recorder.start(500);
                    setMediaRecorder(recorder);
                    setIsRecording(true);
                  }
                }
                break;
              }
              // Transcription events
              case 'transcription_start': {
                // Ephemeral UI signal; do not insert persistent chat message
                if (transcriptionTimeoutRef.current) {
                  clearTimeout(transcriptionTimeoutRef.current);
                  transcriptionTimeoutRef.current = null;
                }
                setCurrentThinkingState('🎙️ Transcribing...');
                setIsLoading(true);
                // Auto-clear after timeout to prevent infinite indicator
                transcriptionTimeoutRef.current = setTimeout(() => {
                  setCurrentThinkingState('');
                  setIsLoading(false);
                  transcriptionTimeoutRef.current = null;
                }, 10000);
                break;
              }
              case 'transcription_complete': {
                const txt = message?.text || '';
                // Clear ephemeral state
                if (transcriptionTimeoutRef.current) {
                  clearTimeout(transcriptionTimeoutRef.current);
                  transcriptionTimeoutRef.current = null;
                }
                setCurrentThinkingState('');
                setIsLoading(false);
                // Persist final transcription as its own bubble and keep it visible
                handleStreamResponse({ id: Date.now(), type: 'transcription', transcription: txt, streaming: false });
                break;
              }
              // Text streaming
              case 'text_start': {
                // Begin streaming indicator without creating an empty chat bubble
                liveLastResponseIdRef.current = genId();
                setIsLoading(true);
                setCurrentThinkingState('Generating…');
                break;
              }
              case 'text_chunk': {
                const content = message?.content || '';
                if (!content) break;
                handleStreamResponse((prev) => ({
                  ...prev,
                  id: liveLastResponseIdRef.current || prev?.id || genId(),
                  answer: (prev?.answer || '') + content,
                  streaming: true,
                }));
                break;
              }
              case 'text_complete': {
                const fullText = message?.full_text || '';
                
                handleStreamResponse((prev) => ({
                  ...prev,
                  id: liveLastResponseIdRef.current || prev?.id || genId(),
                  answer: fullText,
                  sources: message?.sources || [],
                  streaming: false,
                }));
                
                // Trigger avatar speech for voice input responses
                if (useHeyGen && heygenReady && fullText) {
                  console.log("🎤 [Voice Input] Triggering avatar speech:", fullText.substring(0, 50) + "...");
                  heygenTalk(fullText);
                }
                
                break;
              }
              // Audio streaming
              case 'audio_start': {
                liveAudioChunksRef.current = [];
                break;
              }
              case 'audio_chunk': {
                if (message?.data) {
                  // Accumulate only; progressive playback disabled per decision
                  liveAudioChunksRef.current.push(message.data);
                }
                break;
              }
              // case 'audio_complete': {
              //   // Progressive decode may have failed; provide final playable audio
              //   const combinedBase64 = liveAudioChunksRef.current.filter(Boolean).join('');
              //   liveAudioChunksRef.current = [];
              //   if (combinedBase64.length > 500) {
              //     handleStreamResponse({
              //       id: liveLastResponseIdRef.current || genId(),
              //       type: 'ai',
              //       audio: combinedBase64,
              //       mime: 'auto',
              //       streaming: false,
              //     });
              //   }
              //   break;
              // }

              case 'audio_complete': {
                const combinedBase64 = liveAudioChunksRef.current.filter(Boolean).join('');
                liveAudioChunksRef.current = [];
              
                // Avatar speech is now handled in text_complete, not here
                // Just handle the audio response for UI playback
                if (combinedBase64.length > 500) {
                  handleStreamResponse({
                    id: liveLastResponseIdRef.current || genId(),
                    type: 'ai',
                    audio: combinedBase64,
                    mime: 'auto',
                    streaming: false,
                  });
                }
              
                break;
              }
              
              
              case 'chat_complete': {
                // Clear thinking state and loading when chat is complete
                setIsLoading(false);
                setCurrentThinkingState("");
                break;
              }
              case 'error': {
                handleStreamResponse({ id: genId(), type: 'ai', answer: `⚠️ ${message?.message || 'Streaming error'}`, streaming: false });
                break;
              }
              default:
                break;
            }
          } catch {
            // Ignore non-JSON frames; binary handled separately
          }
        };

        ws.onerror = (e) => {
          try { console.warn('[Voice WS] error', e); } catch { }
          setIsLoading(false);
          setCurrentThinkingState("");
        };

        ws.onclose = () => {
          try { console.info('[Voice WS] closed'); } catch { }
          setIsLoading(false);
          setCurrentThinkingState("");
        };
      } catch (e) {
        console.warn('Failed to initialize voice WS streaming:', e);
        setIsLoading(false);
        setCurrentThinkingState("");
      }

      // MediaRecorder now starts after 'ready_for_audio'
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Could not access microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const handleUserSend = (userMessage) => {
    const newUserMessage = {
      id: Date.now(),
      type: 'user',
      message: userMessage,
      timestamp: new Date().toISOString()
    };
    setChatHistory(prev => [...prev, newUserMessage]);
    setChatMessage("");
  };

  return (
    <>

{/* 
<iframe 
  src="https://embed.liveavatar.com/v1/430229a2-c544-44a2-8748-8c215cababd0" 
  allow="microphone" 
  title="LiveAvatar Embed" 
  style={{ aspectRatio: '16/9' }}
/> */}
      <div className="h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 flex flex-col md:flex-row overflow-hidden">
        {/* Left Sidebar */}
        <AgentSidebar 
          agentData={agentData} 
          useHeyGen={useHeyGen} 
          heygenReady={heygenReady} 
          heygenVideoRef={heygenVideoRef} 
          unlockAudio={unlockAudio} 
          heygenAudioRef={heygenAudioRef} 
          heygenLoading={heygenLoading} 
          heygenError={heygenError} 
          heygenNeedsInteraction={heygenNeedsInteraction} 
          heygenEnableAudio={heygenEnableAudio} 
          audioUnlocked={audioUnlocked}
          heygenStarted={heygenStarted}
          onStartAvatar={startHeyGenAvatar}
          onStopAvatar={stopHeyGenAvatar}
        />
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top Bar - Enhanced */}
          <UploadInfoBar uploadResponse={uploadResponse} />

          {/* Main Content */}
          <div className="flex-1 p-3 md:p-6 overflow-y-auto hide-scrollbar">
            <div className="max-w-4xl mx-auto">
              {/* Session controls */}
              <SessionControls
                voiceGender={voiceGender}
                setVoiceGender={setVoiceGender}
                includeAudio={includeAudio}
                setIncludeAudio={setIncludeAudio}
              />
              {/* Summary Card */}
              <SummaryCard
                hideAISummary={hideAISummary}
                isSummaryExpanded={isSummaryExpanded}
                setIsSummaryExpanded={setIsSummaryExpanded}
                handleDownload={handleDownload}
                playingAudioId={playingAudioId}
                isPlaying={isPlaying}
                handleReadAloud={handleReadAloud}
                handleRestartAudio={handleRestartAudio}
                seekAudio={seekAudio}
                stopAudio={stopAudio}
                uploadResponse={uploadResponse}
                setShowShareModal={setShowShareModal}
              />

              {/* Chat History */}
              <ChatHistory
                chatHistory={chatHistory}
                playingAudioId={playingAudioId}
                isPlaying={isPlaying}
                pauseAudio={pauseAudio}
                playAudioFromUrl={playAudioFromUrl}
                playAudioFromBase64={(b64, id, mime = 'audio/mpeg') =>
                  playAudioFromBase64(b64, id, mime, {
                    audioRef,
                    base64ObjectUrlRef,
                    setPlayingAudioId,
                    setIsPlaying,
                  })}
                seekAudio={seekAudio}
                stopAudio={stopAudio}
                isLoading={isLoading}
                currentThinkingState={currentThinkingState}
                messagesEndRef={messagesEndRef}
                onRestartCurrentAudio={handleRestartAudio}
              />
            </div>
          </div>
          <Toast ref={toast} position="top-right" />
          <ShareModal
            showShareModal={showShareModal}
            setShowShareModal={setShowShareModal}
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            whatsappNumber={whatsappNumber}
            setWhatsappNumber={setWhatsappNumber}
            isSending={isSending}
            handleSendWhatsapp={handleSendWhatsapp}
          />

          {/* Chat Input */}
          <ChatBot
            analyst={agentData}
            onResponse={handleStreamResponse}
            sessionId={sessionId}
            isTalkToAgent={isTalkToAgent}
            isRecording={isRecording}
            setIsRecording={setIsRecording}
            chatMessage={chatMessage}
            setChatMessage={setChatMessage}
            startRecording={startRecording}
            stopRecording={stopRecording}

            includeAudio={includeAudio}
            voiceGender={voiceGender}
            languageCode="en-US"
            useWebSocketChat={true}
            useWebSocketVoice={true}
            onUserSend={handleUserSend}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        </div>
      </div>
    </>
  );
}


